import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '../components/ui';
import { installModalEscapeDismiss } from '../hooks/useModalDismiss';

const DialogContext = createContext(null);

/**
 * App-wide confirm / alert dialogs — never use window.confirm / alert / prompt.
 * Lightweight feedback (success/error) should still prefer toast.
 */
export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);

  useEffect(() => installModalEscapeDismiss(), []);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setDialog({
        mode: 'confirm',
        title: options.title || 'Confirm',
        body: options.body || 'Are you sure?',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        danger: Boolean(options.danger),
        resolve,
      });
    });
  }, []);

  /** OK-only message dialog (replaces window.alert). */
  const alert = useCallback((options = {}) => {
    const normalized =
      typeof options === 'string'
        ? { title: 'Notice', body: options }
        : options;
    return new Promise((resolve) => {
      setDialog({
        mode: 'alert',
        title: normalized.title || 'Notice',
        body: normalized.body || '',
        confirmLabel: normalized.confirmLabel || 'OK',
        cancelLabel: null,
        danger: Boolean(normalized.danger),
        resolve,
      });
    });
  }, []);

  const close = useCallback((result) => {
    setDialog((current) => {
      current?.resolve?.(result);
      return null;
    });
  }, []);

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={Boolean(dialog)}
        title={dialog?.title}
        body={dialog?.body}
        confirmLabel={dialog?.confirmLabel}
        cancelLabel={dialog?.mode === 'alert' ? null : dialog?.cancelLabel}
        danger={dialog?.danger}
        onCancel={() => close(false)}
        onConfirm={() => close(true)}
      />
    </DialogContext.Provider>
  );
};

export const useAppDialog = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error('useAppDialog must be used within DialogProvider');
  }
  return ctx;
};

export default DialogContext;
